import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useInsights, type ActivityInsights } from '../api/insights';
import { addDays, formatDateTime, formatGhs } from '../format';
import { Badge, Empty, ErrorNote, Field, Loading } from '../ui/kit';
import { ChartCard, Columns } from '../ui/charts';
import { PageHead } from './shared';

interface Entry {
  id: string;
  action: string;
  entityType: string;
  entityId: string | null;
  actorId: string | null;
  actorName: string | null;
  actorRole: string | null;
  detail: Record<string, unknown>;
  ip: string | null;
  createdAt: string;
}

const GROUPS: { label: string; match: (a: string) => boolean }[] = [
  { label: 'All', match: () => true },
  { label: 'Money', match: (a) => /^(folio|payment|ledger)\./.test(a) },
  { label: 'Bookings', match: (a) => a.startsWith('booking.') },
  { label: 'Staff and security', match: (a) => a.startsWith('staff.') || a.startsWith('guest.id') },
];

const ROLE: Record<string, string> = { OWNER: 'owner', MANAGER: 'manager', FRONT_DESK: 'front desk', ACCOUNTANT: 'accountant', HOUSEKEEPING: 'housekeeping', RESTAURANT: 'restaurant', KITCHEN: 'kitchen', DRIVER: 'driver', ATTENDANT: 'attendant' };

/** The raw action code, made readable. */
const pretty = (a: string) => a.replace(/[._]/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
const shortDate = (iso: string): string => new Date(`${iso}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

/** Only simple facts are shown; nothing is ever expected to hold credentials (the server scrubs them before writing). */
function summary(d: Record<string, unknown>): string {
  return Object.entries(d)
    .filter(([, v]) => ['string', 'number', 'boolean'].includes(typeof v))
    .slice(0, 5)
    .map(([k, v]) => `${k}: ${typeof v === 'number' && /(amount|total|balance|rate|revenue)$/i.test(k) ? formatGhs(v) : v}`)
    .join(' · ');
}

/** Who did it: the name and role, never a code. Events with no person are the system's own (the scheduled job, public booking requests). */
function Who({ e }: { e: Entry }) {
  if (!e.actorId) return <span>by the system</span>;
  if (!e.actorName) return <span>by a former login</span>;
  return (
    <span>
      by <strong>{e.actorName}</strong>
      {e.actorRole ? <span className="muted"> ({ROLE[e.actorRole] ?? e.actorRole.toLowerCase()})</span> : null}
    </span>
  );
}

export function AuditLog() {
  const { me } = useAuth();
  const [group, setGroup] = useState(0);
  const [action, setAction] = useState('');
  const [person, setPerson] = useState('');
  const today = me?.property.today ?? new Date().toISOString().slice(0, 10);

  const people = useQuery({ queryKey: ['audit', 'people'], queryFn: () => api<{ people: { id: string; name: string; role: string; events: number }[] }>('/audit-log/people').then((r) => r.people) });
  const q = useQuery({
    queryKey: ['audit', action, person],
    queryFn: () => api<{ entries: Entry[] }>(`/audit-log?limit=200${action ? `&action=${encodeURIComponent(action)}` : ''}${person ? `&actorId=${encodeURIComponent(person)}` : ''}`).then((r) => r.entries),
  });
  const chart = useInsights<ActivityInsights>('activity', addDays(today, -13), today);
  const list = (q.data ?? []).filter((e) => GROUPS[group]!.match(e.action));

  return (
    <div className="page">
      <PageHead eyebrow="Security" title="Audit log" />

      <div className="viz">
        <ChartCard
          wide
          title="Events in the last 14 days"
          note={
            <>
              Everything recorded, day by day. More charts (by person, by kind of work, by hour) are under <Link to="/insights">Insights</Link>.
            </>
          }
          table={chart.data ? { head: ['Day', 'Events'], rows: chart.data.byDay.map((x) => [shortDate(x.date), x.count]) } : undefined}
        >
          {chart.data ? <Columns data={chart.data.byDay.map((x) => ({ label: shortDate(x.date), value: x.count }))} valueName="Events" emphasis={false} labelEvery={2} height={150} /> : chart.error ? <ErrorNote error={chart.error} retry={() => void chart.refetch()} /> : <Loading />}
        </ChartCard>
      </div>

      <div className="stack">
        <div className="seg" role="group" aria-label="Show">
          {GROUPS.map((g, i) => (
            <button key={g.label} type="button" aria-pressed={group === i} onClick={() => setGroup(i)}>
              {g.label}
            </button>
          ))}
        </div>
        <div className="grid-2">
          <Field id="au-person" label="Done by">
            <select id="au-person" className="select" value={person} onChange={(e) => setPerson(e.target.value)}>
              <option value="">Everyone</option>
              {(people.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({ROLE[p.role] ?? p.role.toLowerCase()}) · {p.events}
                </option>
              ))}
            </select>
          </Field>
          <Field id="au-act" label="Exact action (optional)" hint="e.g. folio.void, booking.cancel, ledger.night_audit">
            <input id="au-act" className="input" value={action} onChange={(e) => setAction(e.target.value.trim())} autoCapitalize="none" />
          </Field>
        </div>
      </div>
      {q.isLoading ? (
        <Loading />
      ) : q.error ? (
        <ErrorNote error={q.error} retry={() => void q.refetch()} />
      ) : list.length === 0 ? (
        <div className="card flat">
          <Empty icon="inbox" title="Nothing recorded for this view" />
        </div>
      ) : (
        <div className="list">
          {list.map((e) => (
            <div key={e.id} className="item">
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span className="title">{pretty(e.action)}</span>
                <span className="small muted">{formatDateTime(e.createdAt)}</span>
              </div>
              <div className="row small muted">
                <Badge>{e.entityType}</Badge>
                <Who e={e} />
                {e.ip ? <span>· {e.ip}</span> : null}
              </div>
              {summary(e.detail) ? <div className="hint">{summary(e.detail)}</div> : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
